// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext, Parliamentarian } from '../types';
import { partyStyle } from '../parties';
import { fmtDollars, fmtMoney, fmtPercent, fmtQuarter, escapeHtml } from '../format';
import {
  isParliamentarian,
  topBy,
  categoryLeader,
  outliers,
  biggestQuarterJump,
  perMember,
} from '../analysis';

interface Card {
  sev: 'info' | 'warn' | 'alert' | 'good';
  kicker: string;
  title: string;
  big?: string;
  body: string;
  slug?: string;
}

export function renderInsights(root: HTMLElement, ctx: AppContext): void {
  const { data } = ctx;
  const parl = data.people.filter(isParliamentarian);
  const cards: Card[] = [];

  const link = (p: Parliamentarian) => `<a class="link" href="#insights&mp=${encodeURIComponent(p.slug)}">${escapeHtml(p.name)}</a>`;

  // 1. Biggest overall spender
  const top = topBy(parl, (p) => p.total);
  if (top) {
    cards.push({
      sev: 'info', kicker: 'Highest total', title: `${top.name} tops the list`, big: fmtDollars(top.total),
      body: `The ${partyStyle(top.partyCode).name} ${top.chamber === 'Senate' ? 'Senator' : 'member'} has the highest work-expense total of any parliamentarian since the 2022 election. ${link(top)}`,
      slug: top.slug,
    });
  }

  // 2. Biggest single claim
  const claim = data.summary.topClaims[0];
  if (claim) {
    const desc = claim.description && claim.description.toLowerCase() !== 'aggregated total' ? claim.description : `${claim.category} (quarterly total)`;
    cards.push({
      sev: 'info', kicker: 'Largest single item', title: 'Biggest line item on record', big: fmtDollars(claim.amount),
      body: `${escapeHtml(claim.name)} — ${escapeHtml(claim.category)}, ${escapeHtml(fmtQuarter(claim.period))}. <span class="rowsub">${escapeHtml(desc)}</span>`,
      slug: claim.slug || undefined,
    });
  }

  // 3. Travel leader (scheduled + unscheduled + international)
  const travelCats = ['Scheduled Commercial Transport', 'Unscheduled Commercial Transport', 'International Travel'];
  const travelTop = topBy(parl, (p) => travelCats.reduce((s, c) => s + (p.byCategory[c] || 0), 0));
  if (travelTop) {
    const v = travelCats.reduce((s, c) => s + (travelTop.byCategory[c] || 0), 0);
    cards.push({
      sev: 'info', kicker: 'Frequent flyer', title: `${travelTop.name} spends most on flights`, big: fmtDollars(v),
      body: `Highest combined commercial and international transport spend. ${link(travelTop)}`,
      slug: travelTop.slug,
    });
  }

  // 4. Car costs leader
  const carLeader = categoryLeader(parl, 'Other Car Costs');
  if (carLeader && carLeader.value > 0) {
    cards.push({
      sev: 'info', kicker: 'Car costs', title: `${carLeader.person.name} leads on cars`, big: fmtDollars(carLeader.value),
      body: `The most spent on COMCAR and private-plated vehicles. ${link(carLeader.person)}`,
      slug: carLeader.person.slug,
    });
  }

  // 5. Outliers
  const { med, list } = outliers(parl, 2);
  if (list.length) {
    cards.push({
      sev: 'warn', kicker: 'Outliers', title: `${list.length} spend more than double the median`, big: `${list.length}`,
      body: `The median parliamentarian spent ${fmtMoney(med)}. ${list.length} people spent more than twice that — mostly ministers and party leaders, whose duties involve far more travel.`,
    });
  }

  // 6. Party per-member extremes
  const eligibleParties = data.summary.parties.filter((p) => p.members >= 3 && p.code !== 'FMR' && p.code !== 'IND');
  if (eligibleParties.length >= 2) {
    const byAvg = [...eligibleParties].sort((a, b) => b.avg - a.avg);
    const hi = byAvg[0], lo = byAvg[byAvg.length - 1];
    cards.push({
      sev: 'good', kicker: 'Per member', title: `${hi.name} average the most per member`, big: fmtDollars(hi.avg),
      body: `Averaged across their ${hi.members} members. The lowest of the major parties is ${escapeHtml(lo.name)} at ${fmtDollars(lo.avg)} each.`,
    });
  }

  // 7. House vs Senate per member
  const house = data.summary.chambers.find((c) => c.name === 'House');
  const senate = data.summary.chambers.find((c) => c.name === 'Senate');
  if (house && senate) {
    const hpm = perMember(house.total, house.members);
    const spm = perMember(senate.total, senate.members);
    const higher = hpm >= spm ? 'House members' : 'Senators';
    cards.push({
      sev: 'info', kicker: 'Chamber', title: `${higher} spend more per head`,
      body: `House members average ${fmtDollars(hpm)} each; Senators average ${fmtDollars(spm)}. Senators cover a whole state, but House members run electorate offices and travel to Canberra from every corner of the country.`,
    });
  }

  // 8. Biggest quarterly jump
  const jump = biggestQuarterJump(data.summary.quarterly);
  if (jump && jump.delta > 0) {
    cards.push({
      sev: 'warn', kicker: 'Quarter on quarter', title: 'Sharpest rise in total spending', big: `+${fmtPercent(jump.pct)}`,
      body: `Total spending jumped ${fmtDollars(jump.delta)} from ${escapeHtml(fmtQuarter(jump.from.q))} to ${escapeHtml(fmtQuarter(jump.to.q))} — the biggest quarter-on-quarter increase in the window.`,
    });
  }

  root.innerHTML = `
    <div class="view-head">
      <h2>Findings</h2>
      <p>Automatically detected highlights from ${data.summary.meta.quarterCount} quarters of data. These are patterns in published spending, not allegations — most expenses are ordinary costs of the job.</p>
    </div>
    <div class="insight-grid">
      ${cards
        .map(
          (c) => `<div class="insight ${c.sev}">
            <div class="kicker">${escapeHtml(c.kicker)}</div>
            <h3>${c.title}</h3>
            ${c.big ? `<div class="big">${c.big}</div>` : ''}
            <p>${c.body}</p>
          </div>`
        )
        .join('')}
    </div>`;

  // Delegate clicks on the insight-card MP links to the drawer.
  root.querySelectorAll<HTMLAnchorElement>('a.link').forEach((a) => {
    a.addEventListener('click', (e) => {
      const href = a.getAttribute('href') || '';
      const m = /mp=([^&]+)/.exec(href);
      if (m) { e.preventDefault(); ctx.openMp(decodeURIComponent(m[1])); }
    });
  });
}
